import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import applicantService from '../services/applicantService';
import ApplicationTimeline from '../components/application/ApplicationTimeline';
import ConsentForm from '../components/consent/ConsentForm';
import DocumentUpload from '../components/documents/DocumentUpload';
import DocumentList from '../components/documents/DocumentList';
import DocumentViewer from '../components/documents/DocumentViewer';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { statusLabel, OUTCOME_LABELS, REASON_CODE_LABELS } from '../utils/applicationStatus';
import './ApplicantPages.css';

const EMPLOYMENT_OPTIONS = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'contract', label: 'Contract' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'unemployed', label: 'Unemployed' }
];

const DISPUTE_TYPES = [
  { value: 'factual_error', label: 'Something in my report is wrong' },
  { value: 'incomplete_data', label: 'Information is missing' },
  { value: 'incorrect_source', label: 'The source of information is wrong' },
  { value: 'other', label: 'Something else' }
];

const EMPTY_DETAILS = {
  first_name: '', last_name: '', id_number: '', current_address: '', employment_type: '', employer_name: '',
  job_title: '', gross_monthly_income: '', net_monthly_income: '', other_income: '', household_size: '', has_pets: false
};

const NUMERIC = ['gross_monthly_income', 'net_monthly_income', 'other_income', 'household_size'];

const messageFrom = (err, fallback) => {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat().join(' ');
  return err.response?.data?.message || err.message || fallback;
};

const ApplicantApplicationPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [hasIdNumber, setHasIdNumber] = useState(false);
  const [maskedId, setMaskedId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [notice, setNotice] = useState(null); // { type, message }
  const [saving, setSaving] = useState(false);
  const [consenting, setConsenting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [dispute, setDispute] = useState({ dispute_type: '', description: '' });
  const [disputing, setDisputing] = useState(false);

  const load = useCallback(async () => {
    const [tl, profile, docs] = await Promise.all([
      applicantService.timeline(id),
      applicantService.getProfile(id),
      applicantService.listDocuments(id)
    ]);
    setTimeline(tl);
    setDocuments(docs);
    if (profile && profile.first_name !== undefined) {
      setHasIdNumber(!!profile.has_id_number);
      setMaskedId(profile.id_number || '');
      setDetails({
        ...EMPTY_DETAILS,
        ...Object.fromEntries(Object.entries(profile).filter(([k, v]) => k in EMPTY_DETAILS && v !== null)),
        id_number: ''
      });
    }
    if (['decision_recorded', 'disputed', 'closed'].includes(tl.status)) {
      setDecisions(await applicantService.decisions(id));
    }
  }, [id]);

  useEffect(() => {
    load()
      .catch((err) => setLoadError(messageFrom(err, 'This application could not be loaded.')))
      .finally(() => setLoading(false));
  }, [load]);

  const say = (type, message) => setNotice({ type, message });

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setDetails((d) => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const payload = { ...details };
      NUMERIC.forEach((k) => {
        payload[k] = payload[k] === '' ? null : Number(payload[k]);
      });
      if (!payload.id_number) delete payload.id_number;
      await applicantService.saveProfile(id, payload);
      await load();
      say('success', 'Your details are saved. You can come back to this page at any time.');
    } catch (err) {
      say('error', messageFrom(err, 'Your details could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const grantConsent = async (formatted) => {
    setConsenting(true);
    setNotice(null);
    try {
      await applicantService.grantConsent(
        id,
        formatted.map((c) => ({ check_type: c.type, choice: c.granted ? 'granted' : 'refused' }))
      );
      await load();
      say('success', 'Your consent choices are recorded. You can review or withdraw them in My data.');
    } catch (err) {
      say('error', messageFrom(err, 'Your consent could not be saved.'));
    } finally {
      setConsenting(false);
    }
  };

  const uploadDocument = async (file, type, onProgress) => {
    try {
      await applicantService.uploadDocument(id, file, type, onProgress);
    } catch (err) {
      throw new Error(messageFrom(err, 'The file could not be uploaded.'));
    }
    await load();
  };

  const deleteDocument = async (docId) => {
    try {
      await applicantService.deleteDocument(docId);
      await load();
    } catch (err) {
      say('error', messageFrom(err, 'The document could not be deleted.'));
    }
  };

  const openDocument = async (doc) => {
    setViewing({ ...doc, url: null });
    try {
      const blob = await applicantService.documentBlob(doc.id);
      setViewing({ ...doc, url: URL.createObjectURL(blob) });
    } catch (err) {
      setViewing(null);
      say('error', messageFrom(err, 'The document could not be opened.'));
    }
  };

  const closeViewer = () => {
    if (viewing?.url) URL.revokeObjectURL(viewing.url);
    setViewing(null);
  };

  const downloadDocument = (doc) => {
    if (!doc.url) return;
    const link = document.createElement('a');
    link.href = doc.url;
    link.download = doc.filename;
    link.click();
  };

  const submitApplication = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      await applicantService.submit(id);
      await load();
      say('success', 'Your application is submitted. We will tell you when there is an update.');
    } catch (err) {
      say('error', messageFrom(err, 'Your application could not be submitted.'));
    } finally {
      setSubmitting(false);
    }
  };

  const raiseDispute = async (e) => {
    e.preventDefault();
    setDisputing(true);
    setNotice(null);
    try {
      await applicantService.raiseDispute(id, dispute);
      setDispute({ dispute_type: '', description: '' });
      await load();
      say('success', 'Your dispute has been sent. It will be acknowledged and looked at, and you will be told the outcome.');
    } catch (err) {
      say('error', messageFrom(err, 'Your dispute could not be sent.'));
    } finally {
      setDisputing(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (loadError) return <Alert type="error" message={loadError} />;

  const steps = Object.fromEntries((timeline?.steps || []).map((s) => [s.key, s.done]));
  const submitted = !!steps.submitted;
  const status = timeline?.status;
  const canSubmit = steps.details && steps.consent && steps.id && !submitted;
  const canDispute = ['report_ready', 'decision_recorded'].includes(status);

  const documentRows = documents.map((d) => ({
    id: d.id,
    type: d.document_type,
    filename: d.original_filename,
    uploadedAt: d.created_at,
    size: d.file_size || 0,
    status: d.status
  }));

  return (
    <div className="ap-page">
      <p><Link to="/applicant">&larr; My applications</Link></p>
      <div className="ap-title-row">
        <h1>Your application</h1>
        <StatusBadge status={status || ''} text={statusLabel(status)} />
      </div>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <section className="ap-card" aria-labelledby="progress-title">
        <h2 id="progress-title">Where you are</h2>
        <ApplicationTimeline timeline={timeline} />
      </section>

      {decisions.length > 0 && (
        <section className="ap-card" aria-labelledby="decision-title">
          <h2 id="decision-title">Decision</h2>
          {decisions.map((d) => (
            <div key={d.id} className="ap-decision">
              <p><strong>{OUTCOME_LABELS[d.outcome] || d.outcome}</strong></p>
              {(d.reason_codes || []).length > 0 && (
                <ul>
                  {d.reason_codes.map((c) => <li key={c}>{REASON_CODE_LABELS[c] || c}</li>)}
                </ul>
              )}
              {d.reason_notes && <p className="ap-muted">{d.reason_notes}</p>}
            </div>
          ))}
        </section>
      )}

      {!submitted && (
        <>
          <section className="ap-card" aria-labelledby="details-title">
            <h2 id="details-title">1. Your details</h2>
            <form onSubmit={saveDetails} noValidate>
              <div className="ap-row">
                <FormField label="First name" name="first_name" value={details.first_name} onChange={handleChange} required autoComplete="given-name" />
                <FormField label="Last name" name="last_name" value={details.last_name} onChange={handleChange} required autoComplete="family-name" />
              </div>
              <FormField
                label="South African ID number"
                name="id_number"
                value={details.id_number}
                onChange={handleChange}
                required={!hasIdNumber}
                inputMode="numeric"
                maxLength={13}
                placeholder={hasIdNumber ? `Saved (${maskedId}). Leave blank to keep it.` : '13 digits'}
                helperText="Stored encrypted and only used for the checks you agree to."
              />
              <FormField label="Current address" name="current_address" type="textarea" value={details.current_address} onChange={handleChange} required autoComplete="street-address" />
              <div className="ap-row">
                <FormField label="Employment" name="employment_type" type="select" options={EMPLOYMENT_OPTIONS} value={details.employment_type} onChange={handleChange} />
                <FormField label="Job title" name="job_title" value={details.job_title} onChange={handleChange} />
              </div>
              <FormField label="Employer" name="employer_name" value={details.employer_name} onChange={handleChange} />
              <div className="ap-row">
                <FormField label="Gross monthly income (R)" name="gross_monthly_income" type="number" min="0" value={details.gross_monthly_income} onChange={handleChange} inputMode="decimal" />
                <FormField label="Net monthly income (R)" name="net_monthly_income" type="number" min="0" value={details.net_monthly_income} onChange={handleChange} inputMode="decimal" />
              </div>
              <div className="ap-row">
                <FormField label="Other income (R)" name="other_income" type="number" min="0" value={details.other_income} onChange={handleChange} inputMode="decimal" />
                <FormField label="People in household" name="household_size" type="number" min="1" value={details.household_size} onChange={handleChange} inputMode="numeric" />
              </div>
              <label className="ap-check">
                <input type="checkbox" name="has_pets" checked={!!details.has_pets} onChange={handleChange} /> I have pets
              </label>
              <button type="submit" className="btn-primary ap-button" disabled={saving}>
                {saving ? 'Saving...' : 'Save details'}
              </button>
            </form>
          </section>

          <section className="ap-card" aria-labelledby="consent-title">
            <h2 id="consent-title">2. What you agree to be checked</h2>
            {steps.consent ? (
              <p>
                Your consent choices are recorded. You can review or withdraw them at any time in{' '}
                <Link to="/my-data">My data</Link>.
              </p>
            ) : (
              <ConsentForm onSubmit={grantConsent} isSubmitting={consenting} />
            )}
          </section>

          <section className="ap-card" aria-labelledby="docs-title">
            <h2 id="docs-title">3. Your documents</h2>
            <p className="ap-muted">Upload your ID first. Payslips or bank statements help verify income.</p>
            <DocumentUpload onUpload={uploadDocument} />
            <DocumentList documents={documentRows} onView={openDocument} onDelete={deleteDocument} />
          </section>

          <section className="ap-card" aria-labelledby="submit-title">
            <h2 id="submit-title">4. Send it to the landlord</h2>
            <p className="ap-muted">
              You can only submit once your details, required consents and ID document are done.
            </p>
            <button type="button" className="btn-primary ap-button" onClick={submitApplication} disabled={!canSubmit || submitting}>
              {submitting ? 'Submitting...' : 'Submit application'}
            </button>
          </section>
        </>
      )}

      {submitted && documentRows.length > 0 && (
        <section className="ap-card" aria-labelledby="docs-done-title">
          <h2 id="docs-done-title">Documents you provided</h2>
          <DocumentList documents={documentRows} onView={openDocument} onDelete={() => {}} />
        </section>
      )}

      {canDispute && (
        <section className="ap-card" aria-labelledby="dispute-title">
          <h2 id="dispute-title">Something wrong?</h2>
          <p className="ap-muted">
            You can ask for a correction or raise a dispute about your report. It will be acknowledged, looked into,
            and the outcome recorded.
          </p>
          <form onSubmit={raiseDispute}>
            <FormField label="What is the problem?" name="dispute_type" type="select" options={DISPUTE_TYPES} value={dispute.dispute_type} onChange={(e) => setDispute((d) => ({ ...d, dispute_type: e.target.value }))} required />
            <FormField label="Tell us what is wrong" name="description" type="textarea" value={dispute.description} onChange={(e) => setDispute((d) => ({ ...d, description: e.target.value }))} required />
            <button type="submit" className="btn-primary ap-button" disabled={disputing || !dispute.dispute_type || !dispute.description}>
              {disputing ? 'Sending...' : 'Send dispute'}
            </button>
          </form>
        </section>
      )}

      <DocumentViewer document={viewing} isOpen={!!viewing} onClose={closeViewer} canDownload onDownload={downloadDocument} />
    </div>
  );
};

export default ApplicantApplicationPage;
